import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import './CompleteProfile.css';

const API_URL = process.env.REACT_APP_API_URL;

const CompleteProfile = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState({ percentage: 0, suggestions: [] });

  // Form states
  const [basicInfo, setBasicInfo] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    headline: '',
    summary: '',
    profilePicture: ''
  });

  const [contactInfo, setContactInfo] = useState({
    alternateEmail: '',
    alternatePhone: '',
    linkedIn: '',
    github: '',
    portfolio: '',
    website: ''
  });

  const [location, setLocation] = useState({
    currentLocation: {
      city: '',
      state: '',
      country: '',
      pincode: ''
    },
    willingToRelocate: false
  });

  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [resumes, setResumes] = useState([]);

  const [jobPreferences, setJobPreferences] = useState({
    desiredJobTitle: '',
    desiredJobTypes: [],
    desiredIndustries: [],
    expectedSalary: {
      min: '',
      max: '',
      currency: 'INR',
      period: 'yearly'
    },
    noticePeriod: '',
    workMode: []
  });

  const [industryOptions, setIndustryOptions] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);

  const tabList = [
    { key: 'basic', label: 'Basic Info', icon: 'person' },
    { key: 'contact', label: 'Contact', icon: 'call' },
    { key: 'location', label: 'Location', icon: 'location_on' },
    { key: 'experience', label: 'Experience', icon: 'work' },
    { key: 'education', label: 'Education', icon: 'school' },
    { key: 'skills', label: 'Skills', icon: 'lightbulb' },
    { key: 'resume', label: 'Resume', icon: 'description' },
    { key: 'preferences', label: 'Preferences', icon: 'tune' },
  ];

  // Fetch profile and master data on mount
  useEffect(() => {
    fetchProfile();
    fetchCompletion();
    fetchIndustryOptions();
  }, []);

  const fetchIndustryOptions = async () => {
    try {
      setIndustriesLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/master/industry-types`, {
        params: { status: 'active' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndustryOptions(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to load industries');
    } finally {
      setIndustriesLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/candidate/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data.data;
      setProfile(data);

      // Populate form states
      setBasicInfo({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
        gender: data.gender || '',
        headline: data.headline || '',
        summary: data.summary || '',
        profilePicture: data.profilePicture || ''
      });

      setContactInfo({
        alternateEmail: data.alternateEmail || '',
        alternatePhone: data.alternatePhone || '',
        linkedIn: data.linkedIn || '',
        github: data.github || '',
        portfolio: data.portfolio || '',
        website: data.website || ''
      });

      setLocation({
        currentLocation: data.currentLocation || { city: '', state: '', country: '', pincode: '' },
        willingToRelocate: data.willingToRelocate || false
      });

      setExperiences(data.workExperience || []);
      setEducations(data.education || []);
      setSkills(data.skills || []);
      setCertifications(data.certifications || []);
      setProjects(data.projects || []);
      setResumes(data.resumes || []);
      setJobPreferences({
        ...jobPreferences,
        ...data.jobPreferences,
        desiredIndustries: (data.jobPreferences?.desiredIndustries || []).map(i => i._id || i)
      });

    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const fetchCompletion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/candidate/profile/completion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletion(response.data.data);
    } catch (error) {
      console.error('Error fetching completion:', error);
    }
  };

  const updateProfile = async (data) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/candidate/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setBasicInfo({ ...basicInfo, profilePicture: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePicture = () => {
    setBasicInfo({ ...basicInfo, profilePicture: '' });
    setFileInputKey(prev => prev + 1);
  };

  const handleBasicInfoSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!basicInfo.firstName || !basicInfo.lastName || !basicInfo.headline) {
      toast.error('Please fill all required fields (First Name, Last Name, and Headline)');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // gender/dateOfBirth are enum/date fields - empty strings fail validation
      const payload = { ...basicInfo };
      if (!payload.gender) delete payload.gender;
      if (!payload.dateOfBirth) delete payload.dateOfBirth;

      await axios.put(`${API_URL}/candidate/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show success message with step completion info
      toast.success('✅ Step 1: Basic Information completed successfully!', {
        duration: 4000,
        icon: '🎉',
        style: {
          border: '1px solid #10b981',
          padding: '14px 18px',
          color: '#065f46',
          background: '#ecfdf5',
          borderRadius: '12px',
          fontWeight: '500'
        }
      });

      // Check if summary is also filled for extra feedback
      if (basicInfo.summary && basicInfo.summary.length >= 100) {
        toast.success('Great! Your profile summary looks comprehensive.', {
          duration: 3000
        });
      }

      fetchProfile();
      fetchCompletion();

      // Auto-navigate to next tab after successful submission
      setTimeout(() => {
        setActiveTab('contact');
        toast('💡 Tip: Complete Contact Information for better visibility', {
          duration: 3000,
          icon: '📞'
        });
      }, 1500);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update basic information');
    } finally {
      setLoading(false);
    }
  };

  const handleContactInfoSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/candidate/profile`, contactInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Step 2: Contact Information saved successfully!', {
        duration: 4000,
        icon: '📞'
      });

      fetchProfile();
      fetchCompletion();

      // Auto-navigate to next tab
      setTimeout(() => {
        setActiveTab('location');
        toast('💡 Next: Add your location details', {
          duration: 3000,
          icon: '📍'
        });
      }, 1500);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact information');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!location.currentLocation.city || !location.currentLocation.state || !location.currentLocation.country) {
      toast.error('Please fill all required location fields (City, State, Country)');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/candidate/profile`, location, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Step 3: Location details saved successfully!', {
        duration: 4000,
        icon: '📍'
      });

      fetchProfile();
      fetchCompletion();

      // Auto-navigate to next tab
      setTimeout(() => {
        setActiveTab('experience');
        toast('💡 Next: Add your work experience', {
          duration: 3000,
          icon: '💼'
        });
      }, 1500);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  const handleJobPreferencesSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/candidate/profile`, { jobPreferences }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Job Preferences saved successfully!', {
        duration: 4000,
        icon: '🎯'
      });

      fetchProfile();
      fetchCompletion();

      // Check overall profile completion
      const completionResponse = await axios.get(`${API_URL}/candidate/profile/completion`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const completionData = completionResponse.data.data;
      if (completionData.percentage >= 80) {
        toast.success('🎉 Great job! Your profile is looking strong!', {
          duration: 4000
        });
      }

    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update job preferences');
    } finally {
      setLoading(false);
    }
  };

  // Experience handlers
  const addExperience = async (exp) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/candidate/profile/experience`, exp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Work experience added successfully!', {
        duration: 3000,
        icon: '💼'
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to add experience');
    }
  };

  const deleteExperience = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/candidate/profile/experience/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Work experience removed', {
        duration: 2000
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to delete experience');
    }
  };

  // Education handlers
  const addEducation = async (edu) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/candidate/profile/education`, edu, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Education added successfully!', {
        duration: 3000,
        icon: '🎓'
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to add education');
    }
  };

  const deleteEducation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/candidate/profile/education/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Education removed', {
        duration: 2000
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to delete education');
    }
  };

  // Skill handlers
  const addSkill = async (skill) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/candidate/profile/skills`, skill, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Skill added successfully!', {
        duration: 3000,
        icon: '💡'
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to add skill');
    }
  };

  const deleteSkill = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/candidate/profile/skills/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Skill removed', {
        duration: 2000
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to delete skill');
    }
  };

  // Resume handlers
  const uploadResume = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('resume', file);

      await axios.post(`${API_URL}/candidate/profile/resume`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('✅ Resume uploaded successfully!', {
        duration: 3000,
        icon: '📝'
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error(error.response?.data?.message || 'Failed to upload resume');
    }
  };

  const setDefaultResume = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/candidate/profile/resume/${id}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default resume updated', {
        duration: 2000
      });
      fetchProfile();
    } catch (error) {
      toast.error('Failed to set default resume');
    }
  };

  const deleteResume = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/candidate/profile/resume/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resume deleted', {
        duration: 2000
      });
      fetchProfile();
      fetchCompletion();
    } catch (error) {
      toast.error('Failed to delete resume');
    }
  };

  return (
    <div className="candidate-complete-profile">
      {/* Profile Completion Banner */}
      <div className="profile-completion-banner">
        <div className="completion-content">
          <div className="completion-head">
            <div>
              <h2>Complete Your Profile</h2>
              <p className="completion-caption">A complete profile gets up to 5x more recruiter views.</p>
            </div>
            <div className="completion-ring">
              <span>{completion.percentage}%</span>
            </div>
          </div>
          <div className="completion-bar">
            <div
              className="completion-progress"
              style={{ width: `${completion.percentage}%` }}
            ></div>
          </div>
          <p className="completion-text">{completion.percentage}% Complete</p>
          {completion.suggestions.length > 0 && (
            <div className="completion-suggestions">
              <p><strong>Next steps</strong></p>
              <ul>
                {completion.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        {tabList.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? 'tab active' : 'tab'}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="material-symbols-outlined">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <form onSubmit={handleBasicInfoSubmit} className="profile-form">
            <h3>Basic Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={basicInfo.firstName}
                  onChange={(e) => setBasicInfo({...basicInfo, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={basicInfo.lastName}
                  onChange={(e) => setBasicInfo({...basicInfo, lastName: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={basicInfo.dateOfBirth}
                  onChange={(e) => setBasicInfo({...basicInfo, dateOfBirth: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select
                  value={basicInfo.gender}
                  onChange={(e) => setBasicInfo({...basicInfo, gender: e.target.value})}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div className="form-group avatar-group">
              <label htmlFor="profilePicture">Profile Picture</label>
              <div className="avatar-uploader">
                <div className="avatar-preview">
                  {basicInfo.profilePicture ? (
                    <img src={basicInfo.profilePicture} alt="Profile preview" />
                  ) : (
                    <span className="material-symbols-outlined">person</span>
                  )}
                </div>
                <div className="avatar-controls">
                  <input
                    key={fileInputKey}
                    id="profilePicture"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                  />
                  <div className="avatar-buttons">
                    <label htmlFor="profilePicture" className="avatar-btn upload">
                      <span className="material-symbols-outlined">photo_camera</span>
                      {basicInfo.profilePicture ? 'Change Photo' : 'Upload Photo'}
                    </label>
                    {basicInfo.profilePicture && (
                      <button
                        type="button"
                        className="avatar-btn remove"
                        onClick={removeProfilePicture}
                      >
                        <span className="material-symbols-outlined">delete</span>
                        Remove
                      </button>
                    )}
                  </div>
                  <small>JPG, PNG or GIF. Max size 2MB.</small>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Professional Headline *</label>
              <input
                type="text"
                placeholder="e.g., Full Stack Developer | React & Node.js Expert"
                value={basicInfo.headline}
                onChange={(e) => setBasicInfo({...basicInfo, headline: e.target.value})}
                maxLength={200}
              />
              <small>{basicInfo.headline.length}/200 characters</small>
            </div>

            <div className="form-group">
              <label>Professional Summary *</label>
              <textarea
                rows="6"
                placeholder="Write a brief summary about your professional background, skills, and career goals..."
                value={basicInfo.summary}
                onChange={(e) => setBasicInfo({...basicInfo, summary: e.target.value})}
                maxLength={2000}
              ></textarea>
              <small>{basicInfo.summary.length}/2000 characters</small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Basic Info'}
            </button>
          </form>
        )}

        {/* Contact Info Tab */}
        {activeTab === 'contact' && (
          <form onSubmit={handleContactInfoSubmit} className="profile-form">
            <h3>Contact Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Alternate Email</label>
                <input
                  type="email"
                  value={contactInfo.alternateEmail}
                  onChange={(e) => setContactInfo({...contactInfo, alternateEmail: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Alternate Phone</label>
                <input
                  type="tel"
                  value={contactInfo.alternatePhone}
                  onChange={(e) => setContactInfo({...contactInfo, alternatePhone: e.target.value})}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>LinkedIn Profile</label>
                <input
                  type="url"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={contactInfo.linkedIn}
                  onChange={(e) => setContactInfo({...contactInfo, linkedIn: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>GitHub Profile</label>
                <input
                  type="url"
                  placeholder="https://github.com/yourusername"
                  value={contactInfo.github}
                  onChange={(e) => setContactInfo({...contactInfo, github: e.target.value})}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Portfolio Website</label>
                <input
                  type="url"
                  placeholder="https://yourportfolio.com"
                  value={contactInfo.portfolio}
                  onChange={(e) => setContactInfo({...contactInfo, portfolio: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Personal Website</label>
                <input
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={contactInfo.website}
                  onChange={(e) => setContactInfo({...contactInfo, website: e.target.value})}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Contact Info'}
            </button>
          </form>
        )}

        {/* Location Tab */}
        {activeTab === 'location' && (
          <form onSubmit={handleLocationSubmit} className="profile-form">
            <h3>Location Details</h3>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={location.currentLocation.city}
                  onChange={(e) => setLocation({
                    ...location,
                    currentLocation: {...location.currentLocation, city: e.target.value}
                  })}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={location.currentLocation.state}
                  onChange={(e) => setLocation({
                    ...location,
                    currentLocation: {...location.currentLocation, state: e.target.value}
                  })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  value={location.currentLocation.country}
                  onChange={(e) => setLocation({
                    ...location,
                    currentLocation: {...location.currentLocation, country: e.target.value}
                  })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Pincode</label>
                <input
                  type="text"
                  value={location.currentLocation.pincode}
                  onChange={(e) => setLocation({
                    ...location,
                    currentLocation: {...location.currentLocation, pincode: e.target.value}
                  })}
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={location.willingToRelocate}
                  onChange={(e) => setLocation({...location, willingToRelocate: e.target.checked})}
                />
                Willing to relocate
              </label>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Location'}
            </button>
          </form>
        )}

        {/* Experience Tab */}
        {activeTab === 'experience' && (
          <div className="profile-section">
            <h3>Work Experience</h3>
            <ExperienceSection
              experiences={experiences}
              onAdd={addExperience}
              onDelete={deleteExperience}
            />
          </div>
        )}

        {/* Education Tab */}
        {activeTab === 'education' && (
          <div className="profile-section">
            <h3>Education</h3>
            <EducationSection
              educations={educations}
              onAdd={addEducation}
              onDelete={deleteEducation}
            />
          </div>
        )}

        {/* Skills Tab */}
        {activeTab === 'skills' && (
          <div className="profile-section">
            <h3>Skills</h3>
            <SkillsSection
              skills={skills}
              onAdd={addSkill}
              onDelete={deleteSkill}
            />
          </div>
        )}

        {/* Resume Tab */}
        {activeTab === 'resume' && (
          <div className="profile-section">
            <h3>Resume</h3>
            <ResumeSection
              resumes={resumes}
              onUpload={uploadResume}
              onSetDefault={setDefaultResume}
              onDelete={deleteResume}
            />
          </div>
        )}

        {/* Job Preferences Tab */}
        {activeTab === 'preferences' && (
          <form onSubmit={handleJobPreferencesSubmit} className="profile-form">
            <h3>Job Preferences</h3>

            <div className="form-group">
              <label>Desired Job Title</label>
              <input
                type="text"
                placeholder="e.g., Senior Software Engineer"
                value={jobPreferences.desiredJobTitle}
                onChange={(e) => setJobPreferences({...jobPreferences, desiredJobTitle: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Expected Salary Range (Annual)</label>
              <div className="form-row">
                <input
                  type="number"
                  placeholder="Min"
                  value={jobPreferences.expectedSalary.min}
                  onChange={(e) => setJobPreferences({
                    ...jobPreferences,
                    expectedSalary: {...jobPreferences.expectedSalary, min: e.target.value}
                  })}
                />
                <input
                  type="number"
                  className='exp_max_range'
                  placeholder="Max"
                  value={jobPreferences.expectedSalary.max}
                  onChange={(e) => setJobPreferences({
                    ...jobPreferences,
                    expectedSalary: {...jobPreferences.expectedSalary, max: e.target.value}
                  })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notice Period</label>
              <select
                value={jobPreferences.noticePeriod}
                onChange={(e) => setJobPreferences({...jobPreferences, noticePeriod: e.target.value})}
              >
                <option value="">Select Notice Period</option>
                <option value="immediate">Immediate</option>
                <option value="15_days">15 Days</option>
                <option value="1_month">1 Month</option>
                <option value="2_months">2 Months</option>
                <option value="3_months">3 Months</option>
                <option value="more_than_3_months">More than 3 Months</option>
              </select>
            </div>

            <div className="form-group">
              <div className="industry-head">
                <label>Preferred Industries</label>
                <span className="industry-count">
                  {jobPreferences.desiredIndustries.length} selected
                </span>
              </div>

              {industryOptions.length > 6 && (
                <input
                  type="search"
                  placeholder="Search industries..."
                  value={industrySearch}
                  onChange={(e) => setIndustrySearch(e.target.value)}
                  style={{marginBottom:'20px'}}
                />
              )}

              {industriesLoading ? (
                <p className="industry-hint">Loading industries...</p>
              ) : industryOptions.length === 0 ? (
                <p className="industry-hint">No industries available yet.</p>
              ) : (
                <div className="industry-checkboxes">
                  {industryOptions
                    .filter(industry =>
                      industry.name.toLowerCase().includes(industrySearch.trim().toLowerCase())
                    )
                    .map((industry) => (
                      <label key={industry._id} className="industry-checkbox">
                        <input
                          type="checkbox"
                          value={industry._id}
                          checked={jobPreferences.desiredIndustries.includes(industry._id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setJobPreferences({
                              ...jobPreferences,
                              desiredIndustries: checked
                                ? [...jobPreferences.desiredIndustries, industry._id]
                                : jobPreferences.desiredIndustries.filter(id => id !== industry._id)
                            });
                          }}
                        />
                        <span>{industry.name}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Preferences'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Experience Section Component
const ExperienceSection = ({ experiences, onAdd, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    designation: '',
    employmentType: 'full-time',
    location: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      company: '',
      designation: '',
      employmentType: 'full-time',
      location: '',
      startDate: '',
      endDate: '',
      currentlyWorking: false,
      description: ''
    });
    setShowForm(false);
  };

  return (
    <div>
      <button className="btn btn-secondary mb-3" onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Cancel' : '+ Add Experience'}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} className="profile-form mb-4">
          <div className="form-group">
            <label>Company *</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Designation *</label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => setFormData({...formData, designation: e.target.value})}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                disabled={formData.currentlyWorking}
              />
            </div>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.currentlyWorking}
                onChange={(e) => setFormData({...formData, currentlyWorking: e.target.checked})}
              />
              Currently working here
            </label>
          </div>
          <button type="submit" className="btn btn-primary">Add Experience</button>
        </form>
      )}

      <div className="items-list">
        {experiences.map((exp) => (
          <div key={exp._id} className="item-card">
            <div className="item-card-main">
              <h4>{exp.designation}</h4>
              <p className="company-name">{exp.company}</p>
              <p className="date-range">
                {new Date(exp.startDate).toLocaleDateString()} -
                {exp.currentlyWorking ? ' Present' : ` ${new Date(exp.endDate).toLocaleDateString()}`}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => onDelete(exp._id)}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Education Section Component
const EducationSection = ({ educations, onAdd, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    degree: '',
    fieldOfStudy: '',
    institution: '',
    startYear: '',
    endYear: '',
    currentlyStudying: false,
    grade: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      degree: '',
      fieldOfStudy: '',
      institution: '',
      startYear: '',
      endYear: '',
      currentlyStudying: false,
      grade: ''
    });
    setShowForm(false);
  };

  return (
    <div>
      <button className="btn btn-secondary mb-3" onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Cancel' : '+ Add Education'}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} className="profile-form mb-4">
          <div className="form-row">
            <div className="form-group">
              <label>Degree / Qualification *</label>
              <input
                type="text"
                placeholder="e.g., Bachelor of Technology (B.Tech)"
                value={formData.degree}
                onChange={(e) => setFormData({...formData, degree: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Field of Study / Specialization</label>
              <input
                type="text"
                placeholder="e.g., Computer Science & Engineering"
                value={formData.fieldOfStudy}
                onChange={(e) => setFormData({...formData, fieldOfStudy: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>School / College / University *</label>
              <input
                type="text"
                placeholder="e.g., Delhi Technological University"
                value={formData.institution}
                onChange={(e) => setFormData({...formData, institution: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Grade / CGPA / Percentage</label>
              <input
                type="text"
                placeholder="e.g., 8.5 CGPA or 82%"
                value={formData.grade}
                onChange={(e) => setFormData({...formData, grade: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Year (Admission)</label>
              <input
                type="number"
                placeholder="e.g., 2018"
                min="1950"
                max="2100"
                value={formData.startYear}
                onChange={(e) => setFormData({...formData, startYear: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>End Year (Graduation)</label>
              <input
                type="number"
                placeholder="e.g., 2022"
                min="1950"
                max="2100"
                value={formData.endYear}
                onChange={(e) => setFormData({...formData, endYear: e.target.value})}
                disabled={formData.currentlyStudying}
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.currentlyStudying}
                onChange={(e) => setFormData({
                  ...formData,
                  currentlyStudying: e.target.checked,
                  endYear: e.target.checked ? '' : formData.endYear
                })}
              />
              I am currently studying here
            </label>
          </div>

          <button type="submit" className="btn btn-primary">Add Education</button>
        </form>
      )}

      <div className="items-list">
        {educations.map((edu) => (
          <div key={edu._id} className="item-card">
            <div className="item-card-main">
              <h4>{edu.degree}{edu.fieldOfStudy ? ` — ${edu.fieldOfStudy}` : ''}</h4>
              <p className="company-name">{edu.institution}</p>
              <p className="date-range">
                {edu.startYear} - {edu.currentlyStudying ? 'Present' : edu.endYear}
                {edu.grade ? ` • ${edu.grade}` : ''}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => onDelete(edu._id)}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Skills Section Component
const SkillsSection = ({ skills, onAdd, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    proficiency: 'intermediate'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({ name: '', proficiency: 'intermediate' });
    setShowForm(false);
  };

  return (
    <div>
      <button className="btn btn-secondary mb-3" onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Cancel' : '+ Add Skill'}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} className="profile-form mb-4">
          <div className="form-group">
            <label>Skill Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Proficiency</label>
            <select
              value={formData.proficiency}
              onChange={(e) => setFormData({...formData, proficiency: e.target.value})}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary">Add Skill</button>
        </form>
      )}

      <div className="skills-grid">
        {skills.map((skill) => (
          <div key={skill._id} className="skill-tag">
            <span>{skill.name}</span>
            <span className="proficiency-badge">{skill.proficiency}</span>
            <button
              className="delete-btn"
              onClick={() => onDelete(skill._id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Resume Section Component
const ResumeSection = ({ resumes, onUpload, onSetDefault, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validFiles = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a PDF or Word document`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} must be less than 10MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    for (const file of validFiles) {
      try {
        await onUpload(file);
      } catch (error) {
        toast.error(`${file.name} failed to upload`);
      }
    }
    setUploading(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getResumeUrl = (resume) => {
    if (!resume.fileUrl) {
      console.error('Resume fileUrl is missing:', resume);
      return '';
    }

    let url;
    if (resume.fileUrl.startsWith('http://') || resume.fileUrl.startsWith('https://')) {
      url = resume.fileUrl;
    } else {
      const baseUrl = process.env.REACT_APP_API_URL.replace('/api/v1', '');
      const filePath = resume.fileUrl.startsWith('/') ? resume.fileUrl : `/${resume.fileUrl}`;
      url = `${baseUrl}${filePath}`;
    }

    // console.log('Resume URL:', url);
    // console.log('Resume data:', resume);
    return url;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Debug: Log resumes data
  // console.log('Resumes data:', resumes);

  return (
    <div>
      <div className="resume-upload-section">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="resume-upload"
        />



        <label htmlFor="resume-upload" className="btn btn-secondary mb-3">
          <span class="material-symbols-outlined" style={{fontSize:'24px'}}>
          cloud_upload
          </span>
          {uploading ? 'Uploading...' : ' Upload Resume(s)'}
        </label>
        <p className="upload-hint">Accepted formats: PDF, DOC, DOCX (Max 10MB each)</p>
      </div>

      {resumes && resumes.length > 0 ? (
        <div className="items-list">
          {resumes.map((resume) => (
            <div key={resume._id} className="item-card resume-card">
              <div className="resume-header">
                <div className="resume-info">
                  <h4>
                    📄 Resume
                    {resume.isDefault && <span className="default-badge">Default</span>}
                  </h4>
                  <p className="resume-meta">
                    <span>Uploaded: {formatDate(resume.uploadedAt)}</span>
                  </p>
                </div>
              </div>
              <div className="resume-actions">
                {!resume.isDefault && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => onSetDefault(resume._id)}
                  >
                    Set as Default
                  </button>
                )}
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => window.open(getResumeUrl(resume), '_blank', 'noopener,noreferrer')}
                >
                  👁️ View
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this resume?')) {
                      onDelete(resume._id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">
            <span class="material-symbols-outlined" style={{color:'#334155'}}>
              scan_delete
            </span>
          </div>
          <h4>No resumes uploaded yet</h4>
          <p>Upload your resume to improve your profile completion</p>
        </div>
      )}

    </div>
  );
};

export default CompleteProfile;
